import { useEffect, useRef, useState } from "react";
import { Check, X, Volume2, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { speakDevice, stopDevice } from "@/lib/deviceVoice";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const src = (u) => (u?.startsWith("http") ? u : `${BACKEND}${u}`);
const cap = (s = "") => s.charAt(0).toUpperCase() + s.slice(1);

function AnimalChoice({ o, picked, answer, onPick }) {
  const isAns = o.key === answer;
  const cls = !picked ? "border-gray-200 hover:border-amber-300" : isAns ? "border-emerald-400 bg-emerald-50" : picked === o.key ? "border-amber-300 bg-amber-50" : "border-gray-100 opacity-50";
  return (
    <button onClick={() => onPick(o.key)} disabled={!!picked} data-testid={`whosaid-option-${o.key}`}
      className={`rounded-3xl border-4 bg-white p-3 flex flex-col items-center transition-colors ${cls}`}>
      <img src={src(o.image)} alt={o.name} className="w-full aspect-[4/3] object-cover rounded-2xl" />
      <span className="mt-2 font-patient font-black text-xl sm:text-2xl text-gray-800 flex items-center gap-2">
        {cap(o.name)}{picked && isAns && <Check className="w-7 h-7 text-emerald-600" />}{picked === o.key && !isAns && <X className="w-7 h-7 text-amber-600" />}
      </span>
    </button>
  );
}

// « Qui a dit quoi ? » : une réplique de fable, on touche l'animal qui l'a dite.
export default function WhoSaid({ onComplete }) {
  const [rounds, setRounds] = useState(null);
  const [n, setN] = useState(0);
  const [picked, setPicked] = useState(null);
  const good = useRef(0);
  const started = useRef(Date.now());
  const done = useRef(false);
  useEffect(() => {
    let ignore = false;
    api.get("/fables/quiz", { params: { n: 6 } }).then((r) => !ignore && setRounds(r.data)).catch(() => !ignore && setRounds([]));
    return () => { ignore = true; };
  }, []);
  useEffect(() => { rounds?.[n + 1]?.options.forEach((o) => { new Image().src = src(o.image); }); }, [rounds, n]);
  const r = rounds?.[n];
  const say = () => r && speakDevice(`« ${r.text} »`);
  useEffect(() => { setPicked(null); const id = setTimeout(say, 400); return () => { clearTimeout(id); stopDevice(); }; }, [r]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!rounds) return <Loader2 className="w-10 h-10 animate-spin text-emerald-700" />;
  if (!r) return <p className="font-patient text-2xl text-gray-500" data-testid="whosaid-empty">Pas encore de fable publiée pour ce jeu.</p>;
  const pick = (k) => { setPicked(k); if (k === r.answer) good.current += 1; };
  const next = () => {
    if (n + 1 < rounds.length) return setN(n + 1);
    if (done.current) return;
    done.current = true;
    const score = Math.round((good.current / rounds.length) * 100);
    onComplete({ score, accuracy: score, duration: Math.round((Date.now() - started.current) / 1000) });
  };
  const ans = r.options.find((o) => o.key === r.answer);
  return (
    <div className="w-full max-w-4xl soft-in text-center" data-testid="whosaid">
      <p className="font-patient text-xl text-gray-500 mb-2" data-testid="whosaid-progress">Réplique {n + 1} sur {rounds.length}</p>
      <h2 className="font-patient font-black text-3xl text-gray-800 mb-4">Qui a dit cela ?</h2>
      <blockquote className="mx-auto max-w-2xl rounded-3xl bg-amber-50 border-4 border-amber-200 p-6 font-serif-display italic text-2xl sm:text-3xl text-amber-950 mb-3" data-testid="whosaid-quote">« {r.text} »</blockquote>
      <button onClick={say} className="mb-6 inline-flex items-center gap-2 h-12 px-6 rounded-full bg-white border-2 border-amber-200 font-patient font-bold text-amber-800" data-testid="whosaid-listen"><Volume2 className="w-5 h-5" /> Écouter</button>
      <div className="grid grid-cols-3 gap-4">{r.options.map((o) => <AnimalChoice key={o.key} o={o} picked={picked} answer={r.answer} onPick={pick} />)}</div>
      {picked && (
        <div className="mt-8" data-testid="whosaid-feedback">
          <p className="font-patient text-2xl text-gray-700 mb-4">{picked === r.answer ? "Bravo ! " : "C'était "}<strong>{cap(ans?.name)}</strong>, dans « {r.fable} ».</p>
          <button onClick={next} data-testid="whosaid-next" className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white font-patient font-black text-2xl px-10 h-16 transition-colors">
            {n + 1 < rounds.length ? "Réplique suivante" : "Terminer"}
          </button>
        </div>
      )}
    </div>
  );
}
